(function () {
	"use strict";

	/**
	 * Configuration
	 */
	const config = {
		minLength: 2,
		ajaxAction: "custom_search",
		selectors: {
			form: ".search-form",
			input: ".search-field",
			submit: ".search-submit",
			resultsPopup: ".search-results-popup",
		},
		classes: {
			active: "active",
			loading: "loading",
			hasResults: "has-results",
		},
	};

	/**
	 * Shared unique popup - created only once
	 */
	let sharedPopup = null;

	/**
	 * Create the global unique popup
	 */
	function createGlobalPopup() {
		// Only create once
		if (sharedPopup) return;

		// Remove all existing popups
		document
			.querySelectorAll(config.selectors.resultsPopup)
			.forEach((p) => p.remove());

		// Find the first .nav-ctnr in the page
		const navCtnr = document.querySelector(".navbar .nav-ctnr");

		if (!navCtnr) {
			console.error("Nav container not found for global popup");
			return;
		}

		// Create THE unique popup with an attribute to prevent cloning
		sharedPopup = document.createElement("div");
		sharedPopup.className = "search-results-popup";
		sharedPopup.setAttribute("data-no-clone", "true"); // Mark to prevent cloning
		navCtnr.appendChild(sharedPopup);

		console.log("Global popup created in", navCtnr);
	}

	/**
	 * AjaxSearch Class
	 */
	class AjaxSearch {
		constructor(container) {
			this.container = container;
			this.form = container.querySelector(config.selectors.form);

			if (!this.form) return;

			this.input = this.form.querySelector(config.selectors.input);
			this.submitBtn = this.form.querySelector(config.selectors.submit);
			this.currentRequest = null;

			this.init();
		}

		/**
		 * Initialization
		 */
		init() {
			this.attachEventListeners();
		}

		/**
		 * Get the shared popup
		 */
		getPopup() {
			return sharedPopup;
		}

		/**
		 * Attach event listeners
		 */
		attachEventListeners() {
			// Form submission
			this.form.addEventListener("submit", (e) => this.handleSubmit(e));

			// Enter key on input
			this.input.addEventListener("keypress", (e) => {
				if (e.key === "Enter") {
					e.preventDefault();
					this.performSearch(this.input.value);
				}
			});

			// Close on outside click
			document.addEventListener("click", (e) => {
				const popup = this.getPopup();
				if (
					popup &&
					!this.form.contains(e.target) &&
					!popup.contains(e.target)
				) {
					this.closePopup();
				}
			});

			// Close on ESC key
			document.addEventListener("keydown", (e) => {
				if (e.key === "Escape") {
					this.closePopup();
				}
			});
		}

		/**
		 * Handle form submission
		 */
		handleSubmit(e) {
			e.preventDefault();
			const searchTerm = this.input.value.trim();
			this.performSearch(searchTerm);
		}

		/**
		 * Perform Ajax search
		 */
		performSearch(searchTerm) {
			// Validation
			if (searchTerm.length < config.minLength) {
				this.showError(ajaxSearchData.strings.min_chars);
				return;
			}

			// Cancel previous request if it exists
			if (this.abortController) {
				this.abortController.abort();
			}

			// Show loading state
			this.showLoading();

			// Create AbortController for fetch
			this.abortController = new AbortController();

			// Build form data
			const formData = new FormData();
			formData.append('action', config.ajaxAction);
			formData.append('nonce', ajaxSearchData.nonce);
			formData.append('search_term', searchTerm);

			// Fetch request (vanilla JS replacement for $.ajax)
			this.currentRequest = fetch(ajaxSearchData.ajaxurl, {
				method: 'POST',
				body: formData,
				signal: this.abortController.signal
			})
			.then(response => response.json())
			.then(data => this.handleSuccess(data))
			.catch(error => {
				if (error.name !== 'AbortError') {
					this.handleError(error);
				}
			})
			.finally(() => {
				this.currentRequest = null;
				const popup = this.getPopup();
				if (popup) {
					popup.classList.remove(config.classes.loading);
				}
			});
		}

		/**
		 * Handle successful request
		 */
		handleSuccess(response) {
			if (!response.success) {
				this.showError(
					response.data.message || ajaxSearchData.strings.error
				);
				return;
			}

			const { total, results, enabled_types } = response.data;

			if (total === 0) {
				this.showNoResults();
			} else {
				this.renderResults(total, results, enabled_types);
			}
		}

		/**
		 * Handle Ajax errors
		 */
		handleError(xhr) {
			if (xhr.statusText === "abort") {
				return; // Request cancelled, do nothing
			}

			this.showError(ajaxSearchData.strings.error);
		}

		/**
		 * Show loading state
		 */
		showLoading() {
			const popup = this.getPopup();
			if (!popup) return;

			popup.classList.add(config.classes.loading, config.classes.active);
			popup.innerHTML = `
                <div class="search-results-loading">
                    <div class="loading-spinner"></div>
                    <p>${ajaxSearchData.strings.searching}</p>
                </div>
            `;
		}

		/**
		 * Show error message
		 */
		showError(message) {
			const popup = this.getPopup();
			if (!popup) return;

			popup.classList.add(config.classes.active);
			popup.classList.remove(
				config.classes.loading,
				config.classes.hasResults
			);
			popup.innerHTML = `
                <div class="search-results-error">
                    <button class="close-results" type="button" aria-label="Close results">×</button>
                    <p>${message}</p>
                </div>
            `;

			// Attach close listener
			const closeBtn = popup.querySelector(".close-results");
			if (closeBtn) {
				closeBtn.addEventListener("click", () => this.closePopup());
			}
		}

		/**
		 * Show "no results" message
		 */
		showNoResults() {
			const popup = this.getPopup();
			if (!popup) return;

			popup.classList.add(config.classes.active);
			popup.classList.remove(
				config.classes.loading,
				config.classes.hasResults
			);
			popup.innerHTML = `
                <div class="search-results-empty">
                    <button class="close-results" type="button" aria-label="Close results">×</button>
                    <p>${ajaxSearchData.strings.no_results}</p>
                </div>
            `;

			// Attach close listener
			const closeBtn = popup.querySelector(".close-results");
			if (closeBtn) {
				closeBtn.addEventListener("click", () => this.closePopup());
			}
		}

		/**
		 * Render search results
		 */
		renderResults(total, results, enabledTypes) {
			const popup = this.getPopup();
			if (!popup) return;

			// Start 2-column wrapper
			let html = `
                <div class="search-results-wrapper">
                    <div class="search-results-left">
                        <div class="search-results-header">
                            <span class="results-count">${total} result${
				total > 1 ? "s" : ""
			} found</span>
                            <button class="close-results" type="button" aria-label="Close results">×</button>
                        </div>
                        <div class="search-results-content">
            `;

			// Type labels
			const typeLabels = {
				page: "Pages",
				post: "News & Logs",
				cruise: "Cruises",
				destination: "Destinations",
			};

			// Check if there are cruises to show the button
			const hasCruises = results.cruises && results.cruises.length > 0;

			// Define custom order: cruises first, then pages, then posts, then destinations
			const typeOrder = ["cruise", "page", "post", "destination"];
			const sortedTypes = [...enabledTypes].sort((a, b) => {
				const indexA = typeOrder.indexOf(a);
				const indexB = typeOrder.indexOf(b);
				return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
			});

			// Group by type
			sortedTypes.forEach((type) => {
				const typeKey = type + "s"; // 'page' to 'pages'
				const items = results[typeKey];

				if (items && items.length > 0) {
					html += this.renderGroup(
						typeLabels[type] || type,
						items,
						type === "cruise" && hasCruises
					);
				}
			});

			html += `
                        </div>
                    </div>
                    <div class="search-results-right">
                        ${this.renderShipsContent()}
                    </div>
                </div>
            `;

			popup.innerHTML = html;
			popup.classList.add(
				config.classes.active,
				config.classes.hasResults
			);
			popup.classList.remove(config.classes.loading);

			// Attach close listener
			const closeBtn = popup.querySelector(".close-results");
			if (closeBtn) {
				closeBtn.addEventListener("click", () => this.closePopup());
			}
		}

		/**
		 * Render a results group
		 */
		renderGroup(label, items, showCruiseButton = false) {
			let html = `
                <div class="results-group">
                    <h3 class="group-title bm__h4">${label} (${items.length})</h3>
                    <ul class="results-list">
            `;

			items.forEach((item) => {
				html += `
                    <li class="result-item">
                        <a aria-label="${item.title}" href="${item.url}">${item.title}</a>
                    </li>
                `;
			});

			html += "</ul>";

			// Add "Go to cruise calendar" button if this is the cruises group and Pontoon is active
			if (
				showCruiseButton &&
				ajaxSearchData.pontoonActive &&
				ajaxSearchData.cruiseCalendarUrl
			) {
				html += `
                    <a aria-label="Go to cruise calendar" href="${ajaxSearchData.cruiseCalendarUrl}" class="cta -outline -primary">
                        Go to cruise calendar
                    </a>
                `;
			}

			html += "</div>";
			return html;
		}

		/**
		 * Render fixed content for right column (Ships)
		 */
		renderShipsContent() {
			const hebrideanLink = ajaxSearchData.hebrideanPrincessLink;
			const highlandsLink = ajaxSearchData.lordOfTheHighlandsLink;

			const hebrideanUrl =
				hebrideanLink && hebrideanLink.url ? hebrideanLink.url : "#";
			const highlandsUrl =
				highlandsLink && highlandsLink.url ? highlandsLink.url : "#";

			return `
                <h3 class="ships-title">Learn more about our ships</h3>
                <div class="ship-cards">
                    <div class="ship-card -hebridean-princess">
                        <div class="ship-card-content">
                            <h4 class="bm__h3">Hebridean Princess</h4>
                            <a aria-label="Explore" href="${hebrideanUrl}" class="btn-explore cta">Explore</a>
                        </div>
                    </div>
                    <div class="ship-card -lord-highlands">
                        <div class="ship-card-content">
                            <h4 class="bm__h3">Lord of the Highlands</h4>
                            <a aria-label="Explore" href="${highlandsUrl}" class="btn-explore cta">Explore</a>
                        </div>
                    </div>
                </div>
            `;
		}

		/**
		 * Close popup
		 */
		closePopup() {
			const popup = this.getPopup();
			if (!popup) return;

			popup.classList.remove(
				config.classes.active,
				config.classes.hasResults
			);
		}
	}

	/**
	 * Initialization on DOM load
	 */
	function initAjaxSearch() {
		// Create global popup ONLY ONCE
		createGlobalPopup();

		// Initialize on normal navbar form
		const normalNavbar = document.querySelector(".navbar");
		if (normalNavbar) {
			new AjaxSearch(normalNavbar);
		}

		// Observe fixed navbar addition (created by navbar.js on scroll)
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.id === "fixed-navbar") {
						new AjaxSearch(node);
					}
				});
			});
		});

		observer.observe(document.body, {
			childList: true,
			subtree: false,
		});
	}

	// Init on DOM ready (vanilla JS)
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAjaxSearch);
	} else {
		initAjaxSearch();
	}
})();
